import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MessageCircle, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMensagemCompartilhamento, useMarcarEnviado } from "@/hooks/use-mercado";

/**
 * Reenviar o romaneio / o convite / o lembrete pelo WhatsApp do produtor.
 *
 * Reusa o fluxo do C1: o texto vem PRONTO do servidor (`/mensagens`), o botão abre
 * o wa.me e marca como enviado no MESMO gesto (grava um novo ENVIADO — o histórico
 * é append-only). Nada aqui dispara mensagem sozinho.
 */
export function DialogReenviar({
  compartilhamentoId,
  titulo,
  descricao,
  primeiroEnvio,
  onFechar,
}: {
  compartilhamentoId: string;
  titulo: string;
  descricao?: string;
  /** Nunca enviado: o botão diz "Enviar", não "Reenviar" — não é lembrete. */
  primeiroEnvio?: boolean;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const { data: mensagem, isLoading } = useMensagemCompartilhamento(compartilhamentoId);
  const marcarEnviado = useMarcarEnviado();

  function handleEnviar() {
    marcarEnviado.mutate(compartilhamentoId, {
      onError: (e: any) => toast({
        title: "Abri o WhatsApp, mas não consegui marcar como enviado",
        description: e.message,
        variant: "destructive",
      }),
    });
  }

  async function handleCopiar() {
    if (!mensagem) return;
    try {
      await navigator.clipboard.writeText(mensagem.texto);
      toast({ title: "Texto copiado!", description: "Cole na conversa com o corretor." });
    } catch (e: any) {
      toast({ title: "Não consegui copiar", description: e.message, variant: "destructive" });
    }
  }

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) onFechar(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {descricao && <DialogDescription>{descricao}</DialogDescription>}
        </DialogHeader>

        {isLoading || !mensagem ? (
          <p className="text-xs text-muted-foreground">Montando a mensagem…</p>
        ) : (
          <div className="rounded-lg rounded-tl-sm bg-sucesso-suave text-foreground text-[13px] leading-relaxed p-3 whitespace-pre-wrap break-words border border-sucesso/20">
            {mensagem.texto}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="min-h-11" onClick={handleCopiar} disabled={!mensagem} data-testid="button-reenviar-copiar">
            <Copy className="h-4 w-4 mr-2" />Copiar texto
          </Button>
          <Button className="min-h-11" asChild disabled={!mensagem} data-testid="button-reenviar-whatsapp">
            <a href={mensagem?.urlWhatsApp ?? "#"} target="_blank" rel="noopener noreferrer" onClick={handleEnviar}>
              <MessageCircle className="h-4 w-4 mr-2" />
              {primeiroEnvio ? "Enviar pelo meu WhatsApp" : "Reenviar pelo meu WhatsApp"}
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
